import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { useAuth } from "../../contexts/AuthContext";

const ChatIcon = IoChatbubbleEllipsesOutline as any;

// Feedback goes to Sentry's Feedback inbox via captureFeedback (SDK v8+), which
// carries the typed text as the feedback message — the thing the previous
// captureMessage + legacy report dialog never included. The Google Chat
// integration listens on that inbox. The dialog is our own so it follows the
// app's theme and needs no DOM patching of Sentry's embed.

type Phase = "idle" | "sending" | "sent" | "failed";

// What the feedback is about (Trello #537, Osman's request), so it can be routed.
// Sent as the `inquiry_type` tag and as a "[Label]" prefix on the message, which
// is what the Google Chat feed shows.
const TYPES = [
  { id: "data_error", label: "Data error" },
  { id: "not_working", label: "Something isn't working" },
  { id: "feature_request", label: "Feature request" },
  { id: "question", label: "Question" },
  { id: "other", label: "Other" },
] as const;
type InquiryType = (typeof TYPES)[number]["id"];

const FeedbackButton: React.FC = () => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState("");
  const [type, setType] = useState<InquiryType | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Prefill from the signed-in user each time the dialog opens; both stay editable.
  const openDialog = () => {
    const userEmail = currentUser?.email && currentUser.email !== "[email]" ? currentUser.email : "";
    setName(currentUser?.displayName || (userEmail ? userEmail.split("@")[0] : ""));
    setEmail(userEmail);
    setMessage("");
    setType(null);
    setPhase("idle");
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && phase !== "sending" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, phase]);

  const send = async () => {
    const text = message.trim();
    if (!text || !type || phase === "sending") return; // no empty or untyped reports, no double submits
    const typeLabel = TYPES.find((t) => t.id === type)?.label ?? type;
    setPhase("sending");
    try {
      if (currentUser?.email) Sentry.setUser({ email: currentUser.email, id: currentUser.uid });
      Sentry.captureFeedback(
        {
          message: `[${typeLabel}] ${text}`,
          name: name.trim() || "Anonymous",
          email: email.trim() || undefined,
          url: window.location.href,
          source: "feedback-button",
        },
        {
          includeReplay: true,
          captureContext: {
            tags: { feedback_type: "user_submitted", inquiry_type: type, page_path: window.location.pathname },
          },
        }
      );
      // captureFeedback queues the envelope; wait for it to actually leave before
      // telling the user it's sent (and before re-enabling the button).
      await Sentry.flush(3000);
      setPhase("sent");
      setTimeout(() => setOpen(false), 1500);
    } catch (err) {
      console.error("Feedback failed to send:", err);
      setPhase("failed");
    }
  };

  const sending = phase === "sending";

  return (
    <>
      <button
        onClick={openDialog}
        className="print:hidden fixed bottom-5 right-5 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-on-brand shadow-brand-glow transition-all duration-300 hover:bg-brand-600 hover:-translate-y-0.5 hover:shadow-card-hover"
        title="Report an issue or ask a question"
        aria-label="Send feedback"
      >
        <ChatIcon className="h-5 w-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-scrim/40 p-4 sm:items-center"
          onMouseDown={(e) => e.target === e.currentTarget && !sending && setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            className="w-full max-w-md rounded-2xl border border-paper-300/70 bg-surface p-5 shadow-card"
          >
            <h2 id="feedback-title" className="font-display text-lg font-semibold text-ink">
              Report an issue or ask a question
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              Describe what you saw or what you'd like. Your recent actions on this page are attached automatically.
            </p>

            {phase === "sent" ? (
              <p className="mt-5 rounded-lg bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700">
                Thank you — your feedback has been sent.
              </p>
            ) : (
              <>
                <p className="mt-4 text-xs font-medium text-ink-muted" id="feedback-type-label">
                  What is it about?
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="feedback-type-label">
                  {TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      role="radio"
                      aria-checked={type === t.id}
                      onClick={() => setType(t.id)}
                      disabled={sending}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${
                        type === t.id
                          ? "border-brand-500 bg-brand-500 text-on-brand"
                          : "border-paper-300 text-ink hover:bg-paper-100"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <label className="mt-4 block text-xs font-medium text-ink-muted" htmlFor="feedback-message">
                  What would you like to report?
                </label>
                <textarea
                  id="feedback-message"
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  disabled={sending}
                  className="mt-1 w-full rounded-lg border border-paper-300 bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-60"
                />
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-ink-muted" htmlFor="feedback-name">
                      Name (optional)
                    </label>
                    <input
                      id="feedback-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={sending}
                      className="mt-1 w-full rounded-lg border border-paper-300 bg-surface px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-60"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-muted" htmlFor="feedback-email">
                      Email (optional)
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={sending}
                      className="mt-1 w-full rounded-lg border border-paper-300 bg-surface px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-60"
                    />
                  </div>
                </div>
                {phase === "failed" && (
                  <p className="mt-3 text-sm text-bad-ink">Couldn't send just now — please try again.</p>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    onClick={() => setOpen(false)}
                    disabled={sending}
                    className="rounded-lg border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-paper-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={send}
                    disabled={sending || !message.trim() || !type}
                    className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {sending ? "Sending…" : "Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
